import React, { useCallback, useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import clsx from "clsx";
import { format, isBefore, parseISO, startOfDay } from "date-fns";

import {
  Button,
  IconButton,
  Paper,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  makeStyles,
} from "@material-ui/core";
import Edit from "@material-ui/icons/Edit";
import Block from "@material-ui/icons/Block";
import CheckCircleOutline from "@material-ui/icons/CheckCircleOutline";
import DeleteOutline from "@material-ui/icons/DeleteOutline";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import ResponsiveTable from "../../components/ResponsiveTable";
import Title from "../../components/Title";
import ConfirmationModal from "../../components/ConfirmationModal";
import CompanyModal from "../../components/CompanyModal";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  pill: {
    display: "inline-block",
    padding: "2px 10px",
    borderRadius: theme.borderRadius.pill,
    fontSize: 12,
    fontWeight: 500,
    color: theme.palette.common.white,
  },
  active: { backgroundColor: theme.palette.success.main },
  inactive: { backgroundColor: theme.palette.grey[500] },
  overdue: {
    color: theme.palette.error.main,
    fontWeight: 600,
  },
}));

const Companies = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [companies, setCompanies] = useState([]);
  const [plans, setPlans] = useState([]);
  const [modal, setModal] = useState({ open: false, company: null });
  const [confirm, setConfirm] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const [{ data: companyList }, { data: planList }] = await Promise.all([
        api.get("/companies"),
        api.get("/plans"),
      ]);
      setCompanies(companyList);
      setPlans(planList);
    } catch (err) {
      toastError(err);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const setActive = async (company, active) => {
    try {
      await api.put(`/companies/${company.id}`, { active });
      toast.success(i18n.t("companies.toasts.saved"));
      fetchAll();
    } catch (err) {
      toastError(err);
    }
  };

  const remove = async (company) => {
    try {
      await api.delete(`/companies/${company.id}`);
      toast.success(i18n.t("companies.toasts.deleted"));
      fetchAll();
    } catch (err) {
      toastError(err);
    }
  };

  const handleConfirm = () => {
    if (confirm.action === "deactivate") setActive(confirm.company, false);
    if (confirm.action === "delete") remove(confirm.company);
  };

  const isOverdue = (company) =>
    company.dueDate && isBefore(parseISO(company.dueDate), startOfDay(new Date()));

  return (
    <MainContainer>
      <CompanyModal
        open={modal.open}
        company={modal.company}
        plans={plans}
        onSaved={fetchAll}
        onClose={() => setModal({ open: false, company: null })}
      />
      <ConfirmationModal
        title={
          confirm
            ? i18n.t(
                confirm.action === "delete"
                  ? "companies.actions.delete"
                  : "companies.actions.deactivate"
              )
            : ""
        }
        open={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={handleConfirm}
      >
        {confirm
          ? i18n.t(
              confirm.action === "delete"
                ? "companies.confirmDelete"
                : "companies.confirmDeactivate",
              { name: confirm.company.name }
            )
          : ""}
      </ConfirmationModal>
      <MainHeader>
        <Title>{i18n.t("companies.title")}</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setModal({ open: true, company: null })}
          >
            {i18n.t("companies.buttons.add")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        <ResponsiveTable>
          <TableHead>
            <TableRow>
              <TableCell>{i18n.t("companies.table.name")}</TableCell>
              <TableCell align="center">{i18n.t("companies.table.plan")}</TableCell>
              <TableCell align="center">{i18n.t("companies.table.usage")}</TableCell>
              <TableCell align="center">{i18n.t("companies.table.dueDate")}</TableCell>
              <TableCell align="center">{i18n.t("companies.table.status")}</TableCell>
              <TableCell align="center">{i18n.t("companies.table.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {companies.map((company) => {
              const own = company.id === user.companyId;
              return (
                <TableRow key={company.id}>
                  <TableCell>
                    {company.name}
                    {company.email && (
                      <Typography variant="caption" component="div" color="textSecondary">
                        {company.email}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center" data-label={i18n.t("companies.table.plan")}>
                    {company.plan?.name || i18n.t("companies.noPlan")}
                  </TableCell>
                  <TableCell align="center" data-label={i18n.t("companies.table.usage")}>
                    {i18n.t("companies.usage", company.usage)}
                  </TableCell>
                  <TableCell
                    align="center"
                    data-label={i18n.t("companies.table.dueDate")}
                    className={clsx(isOverdue(company) && classes.overdue)}
                  >
                    {company.dueDate ? format(parseISO(company.dueDate), "dd/MM/yyyy") : "-"}
                    {isOverdue(company) && ` · ${i18n.t("companies.overdue")}`}
                  </TableCell>
                  <TableCell align="center" data-label={i18n.t("companies.table.status")}>
                    <span
                      className={clsx(
                        classes.pill,
                        company.active ? classes.active : classes.inactive
                      )}
                    >
                      {i18n.t(company.active ? "companies.active" : "companies.inactive")}
                    </span>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={i18n.t("companies.actions.edit")}>
                      <IconButton size="small" onClick={() => setModal({ open: true, company })}>
                        <Edit />
                      </IconButton>
                    </Tooltip>
                    {!own && company.active && (
                      <Tooltip title={i18n.t("companies.actions.deactivate")}>
                        <IconButton
                          size="small"
                          onClick={() => setConfirm({ action: "deactivate", company })}
                        >
                          <Block />
                        </IconButton>
                      </Tooltip>
                    )}
                    {!company.active && (
                      <>
                        <Tooltip title={i18n.t("companies.actions.activate")}>
                          <IconButton size="small" onClick={() => setActive(company, true)}>
                            <CheckCircleOutline />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={i18n.t("companies.actions.delete")}>
                          <IconButton
                            size="small"
                            onClick={() => setConfirm({ action: "delete", company })}
                          >
                            <DeleteOutline />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </ResponsiveTable>
      </Paper>
    </MainContainer>
  );
};

export default Companies;
